import * as Yup from 'yup';

// Schema for options
const optionsSchema = Yup.object().shape({
  min: Yup.number()
    .max(Yup.ref('max'), 'Min must be less than max')
    .required('Min is required'),
  max: Yup.number()
    .min(Yup.ref('min'), 'Max must be greater than min')
    .required('Max is required'),
});

/**
 * Length Schema
 *
 * Defines the validation schema for the length object. The length value can be a number
 * or a random value defined by min/max. The creationType determines if it is a 'RANDOM' or 'CONSTANT' value.
 * The generatorType will always be 'NUMBER' in this context, but can be 'NONE', 'INTEGER', 'BOOLEAN', 'LIST', or 'NUMBER'.
 */
const lengthSchema = Yup.object().shape({
  creationType: Yup.string().required('Creation type is required'),
  generatorType: Yup.number().required('Generator type is required'),
  value: Yup.number().when('creationType', {
    is: 'CONSTANT',
    then: Yup.number().required('Value is required.'),
    otherwise: Yup.number().notRequired(),
  }),
  options: Yup.object().when('creationType', {
    is: 'RANDOM',
    then: optionsSchema.required('Options are required for RANDOM type'),
    otherwise: Yup.object().notRequired(),
  }),
});

/**
 * Room Schema
 *
 * Defines the validation schema for a room object.
 * - id: A string representing the room ID.
 * - number: A number representing the room number.
 * - label: A string representing the room label.
 * - width: A number representing the room width, must be greater than 0.
 * - spaceFunctionId: A string representing the space function ID.
 * - length: A nested object following the lengthSchema.
 * - isVariableLength: A boolean indicating if the length is variable.
 */
const roomSchema = Yup.object().shape({
  id: Yup.string().required('ID is required'),
  number: Yup.number().required('Number is required'),
  label: Yup.string().required('Label is required'),
  width: Yup.number()
    .required('Width is required')
    .moreThan(0, 'Width must be greater than 0'),
  spaceFunctionId: Yup.string().required('Space function ID is required'),
  length: lengthSchema.required('Length is required'),
  isVariableLength: Yup.boolean().required('Variable length flag is required'),
});

/**
 * Get Layout Row Schema
 *
 * Returns the validation schema for a layout row.
 * - numberOfRooms: A number representing the number of rooms, must be greater than 0.
 * - width: A number representing the width of the row, must be greater than 0.
 * - rooms: An array of room objects following the roomSchema.
 *
 * @returns {Yup.ObjectSchema} - The validation schema for a layout row.
 */
const getLayoutRowSchema = () => {
  return Yup.object().shape({
    numberOfRooms: Yup.number()
      .required('Number of rooms is required')
      .moreThan(0, 'Number of rooms must be greater than 0'),
    width: Yup.number()
      .required('Width is required')
      .moreThan(0, 'Width must be greater than 0'),
    rooms: Yup.array().of(roomSchema).required('Rooms are required'),
  });
};

/**
 * Layout Validation Schema
 *
 * Defines the validation schema for the entire layout.
 * - rowFirst: A layout row object following the getLayoutRowSchema, representing the first row.
 * - corridor: A mixed type representing the corridor.
 * - rowSecond: A layout row object following the getLayoutRowSchema, representing the second row.
 */
export const layoutSchema = Yup.object().shape({
  rowFirst: getLayoutRowSchema().required('Row first is required'),
  corridor: Yup.mixed().required('Corridor is required'),
  rowSecond: getLayoutRowSchema().required('Row second is required'),
});

/**
 * Type inference from Yup schema
 *
 * Infers the TypeScript type based on the layoutSchema.
 *
 * @typedef {Object} LayoutValidationSchema
 * @property {LayoutRowSchema} rowFirst - The first row schema.
 * @property {any} corridor - The corridor schema.
 * @property {LayoutRowSchema} rowSecond - The second row schema.
 */
type LayoutValidationSchema = Yup.InferType<typeof layoutSchema>;
